import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Volume2, MicOff, Mic, Play, Clock, Settings, Upload, AlertCircle, Sun, Moon } from 'lucide-react';
import { useElevenLabs } from '../hooks/useElevenLabs';
import { useTheme } from '../context/ThemeContext';
import CurrentRoutineHeader from '../components/CurrentRoutineHeader';
import PerformanceSchedule from '../components/PerformanceSchedule';
import EmceeControls from '../components/EmceeControls';
import AnnouncementTemplateManager from '../components/AnnouncementTemplates';
import Papa from 'papaparse';

// Constants for local storage keys
const STORAGE_KEYS = {
  DARK_MODE: "competition_dark_mode",
};

const EmceePage = () => {
  const { competitionId } = useParams();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const isDarkMode = theme === 'dark';
  const [routines, setRoutines] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [autoPlayAnnouncement, setAutoPlayAnnouncement] = useState(false);
  const [lastAnnouncedRoutine, setLastAnnouncedRoutine] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [announcing, setAnnouncing] = useState(false);
  const [showRightPanel, setShowRightPanel] = useState(true);
  const [activeTab, setActiveTab] = useState('controls'); // 'controls' or 'templates'
  const [csvError, setCsvError] = useState(null);
  const [scheduleSource, setScheduleSource] = useState('none'); // 'none', 'local', 'csv'
  const fileInputRef = useRef(null);
  
  // Save theme preference to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.DARK_MODE, isDarkMode);
  }, [isDarkMode]);
  
  const { speak, isLoading } = useElevenLabs();

  // Handle routines loading
  const handleRoutinesLoad = useCallback((loadedRoutines) => {
    console.log("[EmceePage] Routines loaded:", loadedRoutines);
    setRoutines(loadedRoutines);
    if (loadedRoutines.length > 0 && currentIndex >= loadedRoutines.length) {
      setCurrentIndex(0);
    }
  }, [currentIndex]);

  // Handle routine selection
  const handleRoutineSelect = useCallback((index) => {
    console.log("[EmceePage] Selected routine index:", index);
    setCurrentIndex(index);
    
    // Update routine status
    const updatedRoutines = [...routines];
    
    // Mark all routines with index < selected as completed
    for (let i = 0; i < updatedRoutines.length; i++) {
      if (i < index) {
        updatedRoutines[i] = { ...updatedRoutines[i], status: 'completed' };
      } else if (i === index) {
        updatedRoutines[i] = { ...updatedRoutines[i], status: 'current' };
      } else {
        updatedRoutines[i] = { ...updatedRoutines[i], status: 'upcoming' };
      }
    }
    
    setRoutines(updatedRoutines);
    
    // Auto-play announcement if enabled
    if (autoPlayAnnouncement && 
        (!lastAnnouncedRoutine || lastAnnouncedRoutine !== updatedRoutines[index].routineNumber)) {
      handleAnnouncement(updatedRoutines[index]);
    }
  }, [routines, autoPlayAnnouncement, lastAnnouncedRoutine]);

  // Routine update handler
  const handleRoutineUpdate = useCallback((routine) => {
    setRoutines(prevRoutines => {
      const updatedRoutines = [...prevRoutines];
      const routineIndex = updatedRoutines.findIndex(
        r => r.routineNumber === routine.routineNumber
      );
      
      if (routineIndex >= 0) {
        updatedRoutines[routineIndex] = {
          ...updatedRoutines[routineIndex],
          ...routine
        };
      }
      
      return updatedRoutines;
    });
  }, []);

  // Handle announcement
  const handleAnnouncement = useCallback(async (routine) => {
    if (!routine) return;
    
    console.log("[EmceePage] Announcing routine:", routine);
    setAnnouncing(true);
    
    try {
      await speak(routine);
      setLastAnnouncedRoutine(routine.routineNumber);
    } catch (error) {
      console.error("[EmceePage] Announcement error:", error);
    } finally {
      setAnnouncing(false);
    }
  }, [speak]);

  // Handle custom announcement
  const handleCustomAnnouncement = useCallback(async (message) => {
    console.log("[EmceePage] Custom announcement:", message);
    setAnnouncing(true);
    
    try {
      // Create a mock routine object with just the text for the TTS
      const mockRoutine = {
        routineNumber: '',
        routineName: message,
        ageGroup: '',
        division: '',
        category: ''
      };
      
      await speak(mockRoutine);
    } catch (error) {
      console.error("[EmceePage] Custom announcement error:", error);
    } finally {
      setAnnouncing(false);
    }
  }, [speak]);

  // Handle timing changes
  const handleTimingChange = useCallback((timingData) => {
    // Just log the timing data for now
    console.log("[EmceePage] Timing change:", timingData);
  }, []);

  // Handle navigation
  const handleNavigatePrevious = useCallback(() => {
    if (currentIndex > 0) {
      handleRoutineSelect(currentIndex - 1);
    }
  }, [currentIndex, handleRoutineSelect]);

  const handleNavigateNext = useCallback(() => {
    if (currentIndex < routines.length - 1) {
      handleRoutineSelect(currentIndex + 1);
    }
  }, [currentIndex, routines, handleRoutineSelect]);

  const currentRoutine = routines[currentIndex] || null;

  // Validate CSV headers
  const validateCSVHeaders = (headers) => {
    const requiredHeaders = ["Routine #", "Routine Name"];
    const missing = requiredHeaders.filter((h) => !headers.includes(h));
    return missing.length === 0 ? null : missing;
  };

  // Handle file upload directly in the EmceePage
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setCsvError(null);
      Papa.parse(file, {
        header: true,
        dynamicTyping: true,
        skipEmptyLines: true,
        complete: (results) => {
          const headers = Object.keys(results.data[0] || {});
          const headerError = validateCSVHeaders(headers);

          if (headerError) {
            setCsvError(`Missing required columns: ${headerError.join(", ")}`);
            return;
          }

          const processed = results.data
            .filter((row) => row["Routine #"] && row["Routine Name"])
            .map((row, idx) => ({
              routineNumber: row["Routine #"]?.toString() || "",
              routineName: row["Routine Name"]?.toString() || "",
              studioCode: row["Studio Code"]?.toString() || "",
              performerName:
                row["Performer Name"]?.toString() ||
                row["Dancer Name"]?.toString() ||
                "",
              ageGroup: row["Age(s)"]?.toString() || "",
              style: row["Style"]?.toString() || row["Type"]?.toString() || "",
              category: row["Category"]?.toString() || "",
              division: row["Performance Division"]?.toString() || "",
              level: row["Level"]?.toString() || "",
              numberOfDancers: row["# of Dancers"] || 1,
              status: idx === 0 ? "current" : "upcoming",
            }));

          const sorted = processed.sort((a, b) => {
            const aNum = parseInt(a.routineNumber) || 0;
            const bNum = parseInt(b.routineNumber) || 0;
            return aNum - bNum;
          });

          console.log("[EmceePage] CSV loaded with routines:", sorted);
          
          // Update local state
          setRoutines(sorted);
          setCurrentIndex(0);
          setScheduleSource('csv');
        },
        error: (err) => {
          console.error("Error parsing CSV:", err);
          setCsvError("Failed to parse CSV file. Please check the file format.");
        },
      });
    }
  };

  // Load saved schedule from localStorage on initial load
  useEffect(() => {
    if (competitionId) {
      try {
        const savedData = localStorage.getItem(`emcee_schedule_${competitionId}`);
        if (savedData) {
          const { routines: savedRoutines, currentIndex: savedIndex, timestamp } = JSON.parse(savedData);
          
          // Only use saved data if we have it and it's less than 24 hours old
          const now = Date.now();
          const isRecent = now - timestamp < 24 * 60 * 60 * 1000;
          
          if (savedRoutines?.length > 0 && isRecent) {
            console.log("[EmceePage] Loaded schedule from localStorage:", savedRoutines.length, "routines");
            setRoutines(savedRoutines);
            setCurrentIndex(savedIndex || 0);
            setScheduleSource('local');
          }
        }
      } catch (error) {
        console.error("[EmceePage] Error loading saved schedule:", error);
      }
    }
  }, [competitionId]);

  // Save schedule to localStorage when it changes
  useEffect(() => {
    if (competitionId && routines.length > 0) {
      try {
        const dataToSave = {
          routines,
          currentIndex,
          timestamp: Date.now()
        };
        localStorage.setItem(`emcee_schedule_${competitionId}`, JSON.stringify(dataToSave));
        console.log("[EmceePage] Saved schedule to localStorage");
      } catch (error) {
        console.error("[EmceePage] Error saving schedule to localStorage:", error);
      }
    }
  }, [competitionId, routines, currentIndex]);

  // Add this new function at the end to get schedule source text
  const getScheduleSourceText = () => {
    if (routines.length === 0) return "No schedule loaded";
    
    switch (scheduleSource) {
      case 'local':
        return `Using saved schedule (${routines.length} routines)`;
      case 'csv':
        return `Using CSV schedule (${routines.length} routines)`;
      default:
        return `Schedule loaded (${routines.length} routines)`;
    }
  };

  return (
    <div className="min-h-screen fixed inset-0 overflow-y-auto bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
        {/* Header with back button */}
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg transition-all hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
            Emcee Dashboard
          </h1>
          
          <div className="ml-auto flex items-center gap-4">
            {/* Dark Mode Toggle */}
            <button
              onClick={() => setTheme(isDarkMode ? 'light' : 'dark')}
              className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-800"
              aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            
            {/* CSV Upload Button */}
            <label className="cursor-pointer">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
              />
              <div
                className="flex items-center gap-2 px-3 py-2 rounded-lg transition-all bg-white hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700"
              >
                <Upload className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-400">Load CSV</span>
              </div>
            </label>
            
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="p-2 rounded-lg transition-all hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <Settings className="h-5 w-5" />
            </button>
          </div>
        </div>
        
        {/* CSV error display */}
        {csvError && (
          <div
            className="mb-4 p-3 rounded-lg flex items-center gap-2 bg-red-50 text-red-600 dark:bg-red-900/50 dark:text-red-200"
          >
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm">{csvError}</span>
          </div>
        )}
        
        {/* Settings panel */}
        {showSettings && (
          <div className="mb-4 p-4 rounded-lg bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold mb-4">Emcee Settings</h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="font-medium">Auto-play announcements</span>
                <button
                  onClick={() => setAutoPlayAnnouncement(!autoPlayAnnouncement)}
                  className={`px-3 py-1.5 rounded-lg transition-all ${
                    autoPlayAnnouncement
                      ? "bg-blue-50 text-blue-600 ring-1 ring-blue-200 dark:bg-blue-500/20 dark:text-blue-400 dark:ring-blue-500/30"
                      : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                  }`}
                >
                  {autoPlayAnnouncement ? 'Enabled' : 'Disabled'}
                </button>
              </div>

              <div className="flex items-center justify-between">
                <span className="font-medium">Show controls panel</span>
                <button
                  onClick={() => setShowRightPanel(!showRightPanel)}
                  className={`px-3 py-1.5 rounded-lg transition-all ${
                    showRightPanel
                      ? "bg-blue-50 text-blue-600 ring-1 ring-blue-200 dark:bg-blue-500/20 dark:text-blue-400 dark:ring-blue-500/30"
                      : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                  }`}
                >
                  {showRightPanel ? 'Visible' : 'Hidden'}
                </button>
              </div>
              
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <h4 className="font-medium mb-3">Schedule Management</h4>
                
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      if (window.confirm('Are you sure you want to clear the schedule? This cannot be undone.')) {
                        setRoutines([]);
                        setCurrentIndex(0);
                        setScheduleSource('none');
                        if (competitionId) {
                          localStorage.removeItem(`emcee_schedule_${competitionId}`);
                        }
                      }
                    }}
                    className={`px-3 py-1.5 text-sm rounded-lg transition-all ${
                      "bg-red-50 text-red-600 dark:bg-red-900/50 dark:text-red-200"
                    }`}
                  >
                    Clear Schedule
                  </button>
                  
                  <button
                    onClick={() => {
                      fileInputRef.current?.click();
                    }}
                    className={`px-3 py-1.5 text-sm rounded-lg transition-all ${
                      "bg-green-50 text-green-600 dark:bg-green-900/50 dark:text-green-200"
                    }`}
                  >
                    Upload CSV
                  </button>
                </div>
              </div>
              
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4 flex justify-end">
                <button
                  onClick={() => setShowSettings(false)}
                  className={`px-4 py-2 rounded-lg ${
                    "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                  }`}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Current routine header */}
        <div className="relative mb-4">
          <CurrentRoutineHeader
            isDarkMode={isDarkMode}
            currentRoutine={currentRoutine}
            canNavigatePrevious={currentIndex > 0}
            canNavigateNext={currentIndex < routines.length - 1}
            onNavigatePrevious={handleNavigatePrevious}
            onNavigateNext={handleNavigateNext}
            loading={isLoading}
            onRoutineUpdate={handleRoutineUpdate}
          />
          
          {/* Announcement controls */}
          <div className="absolute right-8 top-full -mt-4 flex items-center gap-2">
            <button
              onClick={() => currentRoutine && handleAnnouncement(currentRoutine)}
              disabled={isLoading || announcing || !currentRoutine}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 dark:from-purple-600 dark:to-blue-600 dark:hover:from-purple-700 dark:hover:to-blue-700 text-white shadow-lg ${
                (isLoading || announcing || !currentRoutine) ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {announcing ? (
                <>
                  <Mic className="h-5 w-5 animate-pulse" />
                  <span>Announcing...</span>
                </>
              ) : (
                <>
                  <Volume2 className="h-5 w-5" />
                  <span>Announce Now</span>
                </>
              )}
            </button>
            
            <button
              onClick={() => setAutoPlayAnnouncement(!autoPlayAnnouncement)}
              className={`p-2 rounded-full transition-all ${
                autoPlayAnnouncement
                  ? "bg-blue-50 text-blue-600 ring-1 ring-blue-200 dark:bg-blue-500/20 dark:text-blue-400 dark:ring-blue-500/30"
                  : "bg-white text-gray-500 hover:text-gray-700 dark:bg-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              }`}
              title={autoPlayAnnouncement ? 'Auto-announce enabled' : 'Auto-announce disabled'}
            >
              {autoPlayAnnouncement ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Main content */}
        <div className="flex gap-4">
          {/* Performance schedule - left panel */}
          <div className={`${showRightPanel ? 'w-7/12' : 'w-full'}`}>
            <PerformanceSchedule
              isDarkMode={isDarkMode}
              routines={routines}
              currentIndex={currentIndex}
              onRoutineSelect={handleRoutineSelect}
              onRoutinesLoad={handleRoutinesLoad}
              readOnly={false}
              isEmceePage={true}
              performanceMetadata={{
                city: "Main Stage",
                stage: "Emcee View"
              }}
            />
          </div>
          
          {/* Controls - right panel */}
          {showRightPanel && (
            <div className="w-5/12 space-y-4">
              {/* Tabs for different sections */}
              <div className="flex mb-4">
                <button
                  onClick={() => setActiveTab('controls')}
                  className={`flex-1 px-4 py-2 text-center transition-all rounded-t-lg font-medium ${
                    activeTab === 'controls'
                      ? "bg-white border-b-2 border-blue-500 text-gray-800 dark:bg-gray-800 dark:text-white"
                      : "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
                  }`}
                >
                  Quick Controls
                </button>
                <button
                  onClick={() => setActiveTab('templates')}
                  className={`flex-1 px-4 py-2 text-center transition-all rounded-t-lg font-medium ${
                    activeTab === 'templates'
                      ? "bg-white border-b-2 border-blue-500 text-gray-800 dark:bg-gray-800 dark:text-white"
                      : "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
                  }`}
                >
                  Templates
                </button>
              </div>
              
              {activeTab === 'controls' && (
                <EmceeControls 
                  isDarkMode={isDarkMode} 
                  onAnnounce={handleCustomAnnouncement}
                  onTimingChange={handleTimingChange}
                />
              )}
              
              {activeTab === 'templates' && (
                <AnnouncementTemplateManager
                  isDarkMode={isDarkMode}
                  currentRoutine={currentRoutine}
                  onAnnounce={handleCustomAnnouncement}
                />
              )}
            </div>
          )}
        </div>
        
        {/* Footer with quick controls */}
        <div className="mt-4 mb-4 rounded-lg p-4 flex items-center justify-between bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-4">
            <button
              onClick={handleNavigatePrevious}
              disabled={currentIndex <= 0}
              className={`px-4 py-2 rounded-lg transition-all bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 ${
                currentIndex <= 0 ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              Previous
            </button>
            
            <button
              onClick={handleNavigateNext}
              disabled={currentIndex >= routines.length - 1}
              className={`px-4 py-2 rounded-lg transition-all bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 ${
                currentIndex >= routines.length - 1 ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              Next
            </button>
          </div>
          
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-400">
              {getScheduleSourceText()} {routines.length > 0 ? `• ${currentIndex + 1} of ${routines.length}` : ''}
            </span>
          </div>
          
          <button
            onClick={() => currentRoutine && handleAnnouncement(currentRoutine)}
            disabled={isLoading || announcing || !currentRoutine}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white ${
              (isLoading || announcing || !currentRoutine) ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <Play className="h-5 w-5" />
            <span>Announce</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmceePage; 